import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { AccessTime, CheckCircleOutline, ErrorOutline, Refresh, SyncAlt } from "@mui/icons-material";
import { Alert, Box, Button, Card, CircularProgress, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import axiosInstance from "../AxiosInstance";
import EposTabs from "./EposTabs";

interface EposImportStatus {
    state: string;
    lastSuccessUtc: string | null;
    nextExtractionUtc: string | null;
}

type Retailer = "Sainsbury's" | "Waitrose";
type ImportStatuses = Partial<Record<Retailer, EposImportStatus>>;

const unavailableStatus: EposImportStatus = {
    state: "unavailable",
    lastSuccessUtc: null,
    nextExtractionUtc: null
};

const formatDateTime = (value: string | null, emptyLabel = "Not yet") => value
    ? new Intl.DateTimeFormat("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23"
    }).format(new Date(value))
    : emptyLabel;

const statePresentation = (state: string) => {
    switch (state) {
        case "running": return { label: "Import running", icon: <SyncAlt fontSize="small" />, color: "primary.main" };
        case "due": return { label: "Import due", icon: <AccessTime fontSize="small" />, color: "warning.main" };
        case "retryScheduled": return { label: "Retry scheduled", icon: <AccessTime fontSize="small" />, color: "warning.main" };
        case "scheduled": return { label: "Up to date", icon: <CheckCircleOutline fontSize="small" />, color: "success.main" };
        case "disabled": return { label: "Paused", icon: <ErrorOutline fontSize="small" />, color: "text.secondary" };
        case "unavailable": return { label: "Unavailable", icon: <ErrorOutline fontSize="small" />, color: "text.secondary" };
        default: return { label: "Connection setup required", icon: <ErrorOutline fontSize="small" />, color: "text.secondary" };
    }
};

export default function EposImportStatusPage() {
    const [statuses, setStatuses] = useState<ImportStatuses | null>(null);
    const [unmappedCount, setUnmappedCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");
    const statusRequestController = useRef<AbortController | null>(null);
    const hasLoadedStatus = useRef(false);

    const loadStatus = useCallback(async (signal?: AbortSignal, manual = false) => {
        if (manual) setRefreshing(true);
        setError("");
        try {
            const [sainsburysResult, waitroseResult] = await Promise.allSettled([
                axiosInstance.get<EposImportStatus>("/epos/import-status", { signal }),
                axiosInstance.get<EposImportStatus>("/epos/waitrose/import-status", { signal })
            ]);
            if (!signal?.aborted) {
                const updates: ImportStatuses = {};
                let hasFailure = false;
                if (sainsburysResult.status === "fulfilled") {
                    updates["Sainsbury's"] = sainsburysResult.value.data;
                } else {
                    hasFailure = true;
                }

                if (waitroseResult.status === "fulfilled") {
                    updates.Waitrose = waitroseResult.value.data;
                } else if (axios.isAxiosError(waitroseResult.reason) && waitroseResult.reason.response?.status === 404) {
                    updates.Waitrose = unavailableStatus;
                } else {
                    hasFailure = true;
                }

                setStatuses(current => ({ ...(current ?? {}), ...updates }));
                setError(hasFailure ? "Import status could not be loaded. Please try again." : "");
                hasLoadedStatus.current = true;
            }
        } catch {
            if (!signal?.aborted)
                setError("Import status could not be loaded. Please try again.");
        } finally {
            if (!signal?.aborted) {
                setLoading(false);
                setRefreshing(false);
            }
        }
    }, []);

    const refreshStatus = useCallback((manual = false) => {
        statusRequestController.current?.abort();
        const controller = new AbortController();
        statusRequestController.current = controller;
        if (!hasLoadedStatus.current) setLoading(true);
        void loadStatus(controller.signal, manual);
    }, [loadStatus]);

    useEffect(() => {
        const controller = new AbortController();
        refreshStatus();
        void axiosInstance.get<{ count: number }>("/epos/product-mappings/unmapped-count", { params: { retailer: 1 }, signal: controller.signal })
            .then(response => { if (!controller.signal.aborted) setUnmappedCount(response.data.count); })
            .catch(() => undefined);

        const intervalId = window.setInterval(() => refreshStatus(), 30_000);
        return () => {
            controller.abort();
            statusRequestController.current?.abort();
            window.clearInterval(intervalId);
        };
    }, [refreshStatus]);

    const nextImport = (status: EposImportStatus) => status.state === "unavailable"
        ? "-"
        : status.state === "notConfigured"
        ? "After setup"
        : status.state === "disabled"
            ? "-"
            : status.state === "running"
                ? "-"
                : status.state === "due"
                    ? "Begins shortly"
                    : formatDateTime(status.nextExtractionUtc, "Not scheduled");

    const stateDescription = (state: string) => state === "disabled"
        ? "Disabled in Settings > Integrations. Enable it to resume imports."
        : state === "notConfigured"
            ? "Add connection details in Settings > Integrations and enable the import."
            : null;

    return (
        <Box sx={{ width: "100%", maxWidth: 1500, minWidth: 0, mx: "auto", py: { xs: 2, md: 3 }, px: { xs: 1, sm: 2 } }}>
            <EposTabs unmappedCount={unmappedCount} />

            {error && <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => refreshStatus(true)}>Retry</Button>} sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
                <Box sx={{ display: "grid", placeItems: "center", minHeight: 240 }}><CircularProgress /></Box>
            ) : statuses && (
                <Card variant="outlined">
                    <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="flex-end" spacing={1.5} sx={{ px: { xs: 2, sm: 2.5 }, py: 1, borderBottom: 1, borderColor: "divider" }}>
                        <Button variant="outlined" startIcon={refreshing ? <CircularProgress size={16} /> : <Refresh />} onClick={() => refreshStatus(true)} disabled={refreshing} sx={{ alignSelf: { xs: "flex-start", sm: "auto" } }}>
                            Refresh
                        </Button>
                    </Stack>

                    <TableContainer sx={{ overflowX: "auto" }}>
                        <Table aria-label="EPOS import status by retailer" sx={{ minWidth: 680 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Retailer</TableCell>
                                    <TableCell>State</TableCell>
                                    <TableCell>Next import</TableCell>
                                    <TableCell>Last completed</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(["Sainsbury's", "Waitrose"] as const).map(retailer => {
                                    const status = statuses[retailer] ?? unavailableStatus;
                                    const presentation = statePresentation(status.state);
                                    const description = stateDescription(status.state);

                                    return (
                                        <TableRow key={retailer}>
                                            <TableCell component="th" scope="row" sx={{ fontWeight: 650 }}>{retailer}</TableCell>
                                            <TableCell>
                                                <Stack direction="row" alignItems="center" spacing={1} sx={{ color: presentation.color }}>
                                                    {presentation.icon}
                                                    <Typography variant="body2" color="inherit">{presentation.label}</Typography>
                                                </Stack>
                                                {description && <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>{description}</Typography>}
                                            </TableCell>
                                            <TableCell>{nextImport(status)}</TableCell>
                                            <TableCell>{formatDateTime(status.lastSuccessUtc)}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            )}
        </Box>
    );
}
